/**
 * A dot-delimited permission string.
 *
 * Canonical shape is `<module>.<submodule>.<screen>.<action>`, for example
 * `fleet.vehicles.list.view` or `billing.subscriptions.details.edit`. Nothing in
 * this package hardcodes a module prefix — a federated module ships its own
 * namespace and the evaluator treats it identically to a first-party one.
 *
 * Two wildcards are understood, on either the granted or the requested side:
 *   - `*`  matches exactly one segment      (`fleet.vehicles.*.view`)
 *   - `**` matches one or more trailing segments, and may only appear last
 *          (`fleet.**` grants everything under `fleet`)
 */
export type PermissionExpression = string;

export const PERMISSION_SEGMENT_COUNT = 4;

export const SEGMENT_WILDCARD = "*";
export const TAIL_WILDCARD = "**";

/** The four canonical action ids. Modules may define others. */
export const PermissionAction = {
  View: "view",
  Create: "create",
  Update: "update",
  Delete: "delete",
} as const;

export type PermissionActionId =
  (typeof PermissionAction)[keyof typeof PermissionAction];

/**
 * What a module, route or navigation node demands of the current user.
 *
 * The bare-array shorthand means **anyOf** — the user needs at least one of the
 * listed expressions. That matches how navigation visibility has always worked
 * (a rail item is shown when any of its screens is viewable). When you need
 * every expression to hold, use the object form with `allOf`; do not rely on
 * the shorthand to mean conjunction.
 *
 * An empty array, an empty object, or `undefined` all mean "no constraint".
 */
export type PermissionRequirement =
  | readonly PermissionExpression[]
  | {
      readonly anyOf?: readonly PermissionExpression[];
      readonly allOf?: readonly PermissionExpression[];
    };

/** Explicit, self-documenting requirement for unrestricted surfaces. */
export const PUBLIC: PermissionRequirement = [];

/** Builds a conjunctive requirement, since the shorthand is disjunctive. */
export function allOf(
  ...expressions: readonly PermissionExpression[]
): PermissionRequirement {
  return { allOf: expressions };
}

/** Builds a disjunctive requirement explicitly, for symmetry with `allOf`. */
export function anyOf(
  ...expressions: readonly PermissionExpression[]
): PermissionRequirement {
  return { anyOf: expressions };
}

/**
 * A module's contribution to the permission matrix rendered on the roles
 * screen. The backend is the source of truth for which permissions are
 * grantable; this tree supplies structure and translation keys so a module can
 * appear in the matrix without the host knowing about it in advance.
 */
export interface PermissionCatalogAction {
  readonly id: string;
  readonly labelKey: string;
}

export interface PermissionCatalogNode {
  /** One expression segment, e.g. `vehicles`. */
  readonly segment: string;
  readonly labelKey: string;
  readonly children?: readonly PermissionCatalogNode[];
  /** Present on leaf (screen) nodes. */
  readonly actions?: readonly PermissionCatalogAction[];
}

/** Canonical parts of a fully-qualified, wildcard-free expression. */
export interface ParsedPermission {
  readonly module: string;
  readonly submodule: string;
  readonly screen: string;
  readonly action: string;
}
